import React, { Dispatch, SetStateAction, useState } from "react"
import type { IShiftPlanDoc } from "../../typings"
import calendarPlusIcon from "assets/svg/CalendarPlus.svg"
import chevronRightIcon from "assets/svg/chevron-right.svg"
import ShiftPlanListCard from "./ShiftPlanListCard"
import ConfigurationCard, {
  ConfigurationCardPropsCardStatus,
} from "../../components/ConfigurationCard"
import { formatDateReadable } from "utils/formatDate"
import { UseMutateFunction } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import { ShiftPlanBody } from "../../overview/ConfigurationWorkplace"
import CustomEmptyState from "../../overview/component/CustomEmptyState"

function formatDateRange(isoDate: string): string {
  try {
    const d = new Date(isoDate)
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  } catch {
    return "—"
  }
}

interface AnytimePlansProps {
  plans?: IShiftPlanDoc[]
  onArchivePlan?: (planId: number) => void
  onRestorePlan?: (planId: number) => void
  createShift?: Dispatch<SetStateAction<boolean>>
  onEditPlan?: (plan: IShiftPlanDoc) => void
  toggleShiftPlan?: UseMutateFunction<
    AxiosResponse<any, any>,
    any,
    { id: number; body: ShiftPlanBody },
    unknown
  >
  archiveShiftPlan?: UseMutateFunction<
    AxiosResponse<any, any>,
    any,
    { id: number; body: ShiftPlanBody },
    unknown
  >
}

const AnytimePlans: React.FC<AnytimePlansProps> = ({
  plans = [],
  onArchivePlan,
  onRestorePlan,
  onEditPlan,
  toggleShiftPlan,
  archiveShiftPlan,
  createShift,
}) => {
  return (
    <>
      {plans.length === 0 ? (
        <CustomEmptyState
          imageUrl="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/cd082f3e-471a-4300-88d7-75a35bd42bcc.png"
          onClick={() => createShift!(true)}
          title="Create plan"
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {plans.map((plan) => (
            <ConfigurationCard
              key={plan.id}
              date={`${formatDateReadable(plan.createdDate)} `}
              title={plan.title}
              employeeCount={plan.totalEmployees || 0}
              shiftCount={plan.totalShiftPeriods || 0}
              status={
                plan.isDeleted
                  ? plan.isDeleted
                  : (plan.status as ConfigurationCardPropsCardStatus)
              }
              onEdit={() => onEditPlan!(plan)}
              onRestore={() => onRestorePlan!(plan.id)}
              onArchive={() =>
                archiveShiftPlan!({
                  id: plan.id,
                  body: { isDeleted: true },
                })
              }
              onToggle={() =>
                plan.status === "published"
                  ? toggleShiftPlan!({
                      id: plan.id,
                      body: { isActive: false, status: "draft" },
                    })
                  : toggleShiftPlan!({
                      id: plan.id,
                      body: { isActive: true, status: "published" },
                    })
              }
            />
          ))}

          <div className="flex flex-col items-center justify-center gap-2 border-2 border-[#E2E8F0] rounded-lg p-4 min-h-[10rem] hover:bg-[#F8FAFC] transition-colors cursor-pointer">
            <img
              src={calendarPlusIcon}
              width={28}
              height={28}
              alt=""
            />
            <span className="flex items-center gap-1 font-semibold text-sm text-[#0F172B]">
              Create shift plan
              <img
                src={chevronRightIcon}
                width={14}
                height={14}
                alt=""
              />
            </span>
          </div>
        </div>
      )}
    </>
  )
}

export default AnytimePlans
